'use client';

import { shouldIdentifyPostHogUser } from '@/lib/posthog';
import { isBrowserTelemetryEnabled } from '@/lib/telemetry/environment';
import { isTelemetryProtectedRoute } from '@/lib/privacy/private-routes';

// `typeof import()` is the idiomatic type for a module that is only ever loaded
// lazily (see `loadRuntime` below). It is fully erased, so it creates no static
// import / bundle coupling — there is no `import type` equivalent that preserves
// that, hence the scoped rule exception.
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
type TelemetryRuntimeModule = typeof import('@/lib/telemetry/client-runtime');

let runtimeModulePromise: Promise<TelemetryRuntimeModule> | null = null;

function loadRuntime() {
  runtimeModulePromise ??= import('@/lib/telemetry/client-runtime');
  return runtimeModulePromise;
}

function withLoadedRuntime(callback: (runtime: TelemetryRuntimeModule) => void) {
  if (!runtimeModulePromise) return;
  void runtimeModulePromise.then(callback);
}

export function bootstrapTelemetry(pathname: string) {
  if (!isBrowserTelemetryEnabled()) return Promise.resolve();
  if (isTelemetryProtectedRoute(pathname)) return Promise.resolve();
  return loadRuntime().then((runtime) => runtime.ensureClientTelemetryInitialized(pathname));
}

export function captureTelemetryEvent(
  event: string,
  properties: Record<string, string | number | boolean | null | undefined>,
) {
  if (!isBrowserTelemetryEnabled()) return;
  void loadRuntime().then((runtime) => {
    void runtime.capturePostHogEvent(event, properties);
  });
}

type ErrorContext = Record<string, string | number | boolean | null | undefined>;

/**
 * For a caught error the user only sees friendly copy for: the console line is for
 * devtools, and PostHog error tracking keeps the stack. A Server Action error that
 * reached the browser carries a `digest` and was already captured on the server by
 * onRequestError with its real stack, so only the console line is written for it.
 * `context` must be low-risk: IDs, counts, flags and fixed labels, never user text.
 * Never throws, so it is safe inside any catch.
 */
export function reportClientError(scope: string, error: unknown, context: ErrorContext = {}) {
  try {
    const digest = (error as { digest?: unknown } | null)?.digest;
    if (typeof digest === 'string') {
      console.error(`[${scope}]`, error, { ...context, digest });
      return;
    }
    console.error(`[${scope}]`, error, context);
    if (!isBrowserTelemetryEnabled()) return;
    void loadRuntime()
      .then((runtime) => runtime.capturePostHogException(error, { scope, ...context }))
      .catch(() => undefined);
  } catch {
    // Reporting must never replace the error the caller is already handling.
  }
}

export function getTelemetrySessionId(): Promise<string | undefined> {
  if (!isBrowserTelemetryEnabled()) return Promise.resolve(undefined);
  return loadRuntime().then((runtime) => runtime.getPostHogSessionId());
}

export function identifyTelemetryUser(userId: string, cookieString: string) {
  if (!isBrowserTelemetryEnabled()) return;
  if (!shouldIdentifyPostHogUser(cookieString)) {
    withLoadedRuntime((runtime) => {
      void runtime.resetPostHog();
    });
    return;
  }

  void loadRuntime().then((runtime) => {
    void runtime.identifyPostHogUser(userId);
  });
}

export function resetTelemetryUser() {
  if (!isBrowserTelemetryEnabled()) return;
  withLoadedRuntime((runtime) => {
    void runtime.resetPostHog();
  });
}
